import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Trash2, Check, ScanLine } from 'lucide-react';
import { api } from '../../services/apiService';
import { hardwareApi, type DeviceUnitRow, type DeviceUnitReceipt } from '../../services/hardwareApi';
import { toastService } from '../../services/toastService';

/**
 * THE PAIR'S PROOF — the receipts a till and its reader printed together.
 *
 * A test sale is not a fact about a device, it is a fact about a COUPLE: the
 * till rang it, the reader took the card, and the slip that came out says both
 * worked. So the evidence is gathered on the pair's card, against its POS unit
 * (see `DeviceUnitSchema.receipts`), and an order with four pairs keeps four
 * separate proofs instead of one that the last upload overwrote.
 *
 * SEVERAL PHOTOS PER KIND. A receipt is long and a phone is close: the header,
 * the card line and the signature are routinely three shots, and a merchant
 * disputing a charge months later is better served by three than by whichever
 * one somebody judged best at the time. Both kinds accept as many as you take,
 * added in one go from the picker.
 *
 * WHAT THIS DOES NOT DO: it does not decide whether the test PASSED. It
 * collects what was printed. Whether a stage is complete stays with the
 * journey mark the order screen already writes — these are the evidence
 * behind it, not a replacement for it.
 */

const MAX_BYTES = 10 * 1024 * 1024;

const when = (ts: number) =>
  new Date(ts).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });

const randomId = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;

/**
 * ONE PHOTO, WITH A LINK THAT IS SIGNED WHEN IT IS LOOKED AT.
 *
 * The key is what is stored; the URL is minted per view and expires. A stored
 * URL would rot in the record and hand out access to anyone who found it.
 */
const ReceiptThumb: React.FC<{
  tenantId: string;
  receipt: DeviceUnitReceipt;
  onRemove: () => void;
  removing: boolean;
}> = ({ tenantId, receipt, onRemove, removing }) => {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setUrl(null);
    api.uploads.getPresignedUrl(tenantId, receipt.key)
      .then((r) => { if (live) setUrl(r.presignedUrl); })
      .catch(() => { /* the record stands even when the thumbnail will not load */ });
    return () => { live = false; };
  }, [tenantId, receipt.key]);

  return (
    <figure className="m-0 w-[104px]">
      <button
        type="button"
        onClick={() => url && window.open(url, '_blank', 'noopener')}
        disabled={!url}
        aria-label={`View the ${receipt.kind === 'test' ? 'test sale' : 'refund'} receipt full size`}
        className="grid h-[132px] w-[104px] place-items-center overflow-hidden rounded-md border border-stone-200 bg-stone-100 hover:border-[#DC143C] disabled:cursor-default"
      >
        {url
          ? <img src={url} alt="" className="h-full w-full object-cover" />
          : <RubyGemMark size={14} drawing color="currentColor" />}
      </button>
      <figcaption className="mt-1 text-[10px] leading-tight text-stone-400">
        {when(receipt.at)}
        <span className="block truncate" title={receipt.by}>{receipt.by}</span>
      </figcaption>
      <button
        type="button"
        onClick={onRemove}
        disabled={removing}
        className="mt-0.5 inline-flex min-h-[28px] items-center gap-1 text-[10.5px] font-semibold text-stone-400 hover:text-[#DC143C] disabled:opacity-40"
      >
        {removing ? <RubyGemMark size={10} drawing color="currentColor" /> : <Trash2 size={10} />} Remove
      </button>
    </figure>
  );
};

const KIND_LABEL: Record<'test' | 'refund', string> = {
  test: 'Test sale',
  refund: 'Refund',
};

/**
 * SCAN EITHER BOX AND GET BOTH — proved on the pair, not on the order.
 *
 * The point of coupling is that a label on one carton answers "what else goes
 * with this?". That is worth PROVING per pair before the boxes are sealed: a
 * pair whose codes do not resolve is exactly the pair somebody ships half of.
 *
 * It verifies, it does not record a scan. The check is against this pair's own
 * two devices — tracking id, serial or unit id, the same three the order-wide
 * lookup accepts — so a code that belongs to a DIFFERENT pair reads as a miss
 * here rather than as a pass, which is the mistake worth catching.
 */
const PairBarcodeCheck: React.FC<{
  pos: DeviceUnitRow;
  payment?: DeviceUnitRow;
  onVerified?: () => void;
}> = ({ pos, payment, onVerified }) => {
  const [code, setCode] = useState('');
  const [result, setResult] = useState<'hit' | 'miss' | null>(null);
  const [seen, setSeen] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement | null>(null);

  const mine = [pos, payment].filter(Boolean) as DeviceUnitRow[];
  const codesOf = (u: DeviceUnitRow) =>
    [u.trackingId, u.serialNumber, u.unitId].filter(Boolean).map((v) => String(v).toUpperCase());

  const check = () => {
    const typed = code.trim().toUpperCase();
    if (!typed) return;
    const hit = mine.find((u) => codesOf(u).includes(typed));
    setResult(hit ? 'hit' : 'miss');
    setCode('');
    inputRef.current?.focus();
    if (!hit) return;
    /* Both halves scanned is what "this pair is scannable" means — one of two
       proves only that one label prints. */
    const next = new Set(seen).add(hit.unitId);
    setSeen(next);
    if (next.size >= mine.length) onVerified?.();
  };

  const label = (u: DeviceUnitRow) => u.trackingId || u.serialNumber || u.unitId;
  const done = seen.size >= mine.length && mine.length > 0;

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full ${
          done ? 'bg-emerald-500 text-white' : 'bg-stone-200 text-stone-500'}`}
        >
          {done ? <Check size={13} strokeWidth={3} /> : <ScanLine size={13} />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[12.5px] font-bold text-stone-800">Barcode scanning</span>
          <span className="block text-[11px] text-stone-500">
            {done
              ? `Both labels scanned — ${mine.map(label).join(' and ')}.`
              : `Scan each label on this pair. ${seen.size} of ${mine.length} done.`}
          </span>
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <ScanLine size={14} className="shrink-0 text-stone-400" />
        <input
          ref={inputRef}
          value={code}
          onChange={(e) => { setCode(e.target.value); setResult(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); check(); } }}
          placeholder="Scan a label on this pair…"
          autoComplete="off"
          spellCheck={false}
          aria-label="Scan a label on this pair"
          className="min-h-[40px] flex-1 min-w-[10rem] rounded-lg border border-stone-200 px-3 font-mono text-[12.5px] text-stone-800 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/10"
        />
        <button
          type="button"
          onClick={check}
          className="min-h-[40px] rounded-lg border border-stone-300 bg-white px-3 text-[12px] font-semibold text-stone-600 hover:bg-stone-50"
        >
          Check
        </button>
      </div>

      {result === 'hit' && (
        <p className="mt-2 rounded-lg border-l-[3px] border-emerald-500 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-900">
          That is this pair — <span className="font-mono">{label(pos)}</span>
          {payment ? <> ships with <span className="font-mono">{label(payment)}</span></> : null}.
        </p>
      )}
      {result === 'miss' && (
        <p className="mt-2 rounded-lg border-l-[3px] border-amber-500 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
          That code is not on this pair. It may belong to another pair on this order — scanning it here
          would be exactly the mix-up this check exists to catch.
        </p>
      )}
    </div>
  );
};

const PairReceipts: React.FC<{
  tenantId: string;
  orderId: string;
  /** The pair's POS unit — what its receipts are recorded against. */
  unit: DeviceUnitRow;
  /** The reader coupled to it, for the barcode check. */
  payment?: DeviceUnitRow;
  /** Called once both of the pair's labels have scanned. */
  onBarcodeVerified?: () => void;
  /** So the pair's card can reflect a new photo without reloading the order. */
  onUnitChanged?: (unit: DeviceUnitRow) => void;
  /**
   * Called after the FIRST photo of a kind lands, so the order screen can
   * write the stage's journey mark — which is what `receipt_printing` resolves
   * from and what gates dispatch. The evidence lives here; the mark stays
   * where the journey expects it.
   */
  onFirstOfKind?: (kind: 'test' | 'refund') => void;
}> = ({ tenantId, orderId, unit, payment, onUnitChanged, onFirstOfKind, onBarcodeVerified }) => {
  const [busyKind, setBusyKind] = useState<'test' | 'refund' | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const testInput = useRef<HTMLInputElement | null>(null);
  const refundInput = useRef<HTMLInputElement | null>(null);

  const receipts = unit.receipts || [];
  const of = (kind: 'test' | 'refund') => receipts.filter((r) => r.kind === kind);

  /**
   * UPLOAD WHAT WAS CHOSEN, ALL OF IT.
   *
   * Sequential rather than parallel, and deliberately: each add is conditional
   * on the unit's `updatedAt`, so two writes racing each other would have one
   * lose with a 409 and drop a photo the operator believes they uploaded. One
   * at a time is slower and keeps every picture.
   *
   * A file that fails is NAMED. "3 of 4 uploaded" without saying which is worse
   * than useless when the missing one is the signature.
   */
  const upload = useCallback(async (kind: 'test' | 'refund', files: FileList | null) => {
    const chosen = Array.from(files || []);
    if (chosen.length === 0) return;

    const tooBig = chosen.filter((f) => f.size > MAX_BYTES);
    const wrongType = chosen.filter((f) => !f.type.startsWith('image/'));
    if (wrongType.length) {
      toastService.error(`${wrongType.map((f) => f.name).join(', ')} — choose photos (JPG, PNG or HEIC).`);
    }
    if (tooBig.length) {
      toastService.error(`${tooBig.map((f) => f.name).join(', ')} — over 10 MB. Take it again at a lower resolution.`);
    }
    const usable = chosen.filter((f) => f.type.startsWith('image/') && f.size <= MAX_BYTES);
    if (usable.length === 0) return;

    const had = of(kind).length;
    setBusyKind(kind);
    setProgress({ done: 0, total: usable.length });

    let latest = unit;
    const failed: string[] = [];
    for (const [i, file] of usable.entries()) {
      try {
        const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
        const signed = await api.uploads.sign(
          tenantId,
          `${kind}-${randomId()}.${ext}`,
          file.type,
          `admin/orders/${orderId}/receipts`,
        );
        const put = await fetch(signed.uploadUrl, {
          method: 'PUT', headers: { 'Content-Type': file.type }, body: file,
        });
        if (!put.ok) throw new Error(`the upload was refused (${put.status})`);

        const res = await hardwareApi.addReceipt(latest.unitId, {
          kind, key: signed.key, name: file.name.slice(0, 200), contentType: file.type,
        });
        if (res?.unit) { latest = res.unit; onUnitChanged?.(res.unit); }
      } catch (e: any) {
        failed.push(`${file.name} — ${e?.serverMessage || e?.message || 'failed'}`);
      }
      setProgress({ done: i + 1, total: usable.length });
    }

    setBusyKind(null);
    setProgress(null);
    if (testInput.current) testInput.current.value = '';
    if (refundInput.current) refundInput.current.value = '';

    const added = usable.length - failed.length;
    if (added > 0) {
      toastService.success(`${added} ${KIND_LABEL[kind].toLowerCase()} photo${added === 1 ? '' : 's'} saved.`);
      if (had === 0) onFirstOfKind?.(kind);
    }
    failed.forEach((f) => toastService.error(f));
  }, [tenantId, orderId, unit, onUnitChanged, onFirstOfKind]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (receipt: DeviceUnitReceipt) => {
    setRemoving(receipt.receiptId);
    try {
      const res = await hardwareApi.removeReceipt(unit.unitId, receipt.receiptId);
      if (res?.unit) onUnitChanged?.(res.unit);
    } catch (e: any) {
      toastService.error(e?.serverMessage || e?.message || 'Could not remove that photo.');
    } finally {
      setRemoving(null);
    }
  };

  const group = (kind: 'test' | 'refund', inputRef: React.RefObject<HTMLInputElement>) => {
    const mine = of(kind);
    const busy = busyKind === kind;
    return (
      <div className="rounded-xl border border-stone-200 bg-white p-3">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          className="hidden"
          onChange={(e) => upload(kind, e.target.files)}
        />
        <div className="flex flex-wrap items-center gap-2">
          <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full ${
            mine.length ? 'bg-emerald-500 text-white' : 'bg-stone-200 text-stone-500'}`}
          >
            {mine.length ? <Check size={13} strokeWidth={3} /> : <Camera size={13} />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[12.5px] font-bold text-stone-800">{KIND_LABEL[kind]} receipt</span>
            <span className="block text-[11px] text-stone-500">
              {mine.length
                ? `${mine.length} photo${mine.length === 1 ? '' : 's'} on this pair`
                : 'Nothing photographed yet — take as many shots as the slip needs.'}
            </span>
          </span>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-[#DC143C] px-3.5 text-[12px] font-semibold text-white hover:bg-[#9F1239] disabled:opacity-40"
          >
            {busy ? <RubyGemMark size={13} drawing color="currentColor" /> : <Camera size={13} />}
            {busy && progress
              ? `Uploading ${progress.done} of ${progress.total}…`
              : mine.length ? 'Add more photos' : 'Add photos'}
          </button>
        </div>

        {mine.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-3">
            {mine.map((r) => (
              <ReceiptThumb
                key={r.receiptId}
                tenantId={tenantId}
                receipt={r}
                removing={removing === r.receiptId}
                onRemove={() => remove(r)}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-2">
      {group('test', testInput)}
      {group('refund', refundInput)}
      <PairBarcodeCheck pos={unit} payment={payment} onVerified={onBarcodeVerified} />
      <p className="text-[11px] leading-relaxed text-stone-400">
        Recorded against this pair — {unit.trackingId || unit.unitId} and the reader coupled to it. An
        order with several pairs keeps a separate proof for each.
      </p>
    </div>
  );
};

export default PairReceipts;
